import { autoShrinkZone, isPointInZone, isTableLabel } from "../../helpers";
import { Point, Size } from "../../Renderer/types";
import { Zone } from "../../types";
import { CurrentAction, PageSaveState, PageState, State } from "../state";

export interface MouseUpAction {
  ctrl: boolean,
  shift:boolean,
  autoShrink: boolean;
  imageSize: Size;
  location: Point;
  type: "MOUSE UP";
}

const mouseUpOnPage = (currentLabel: string) => (
  state: PageState,
  action: MouseUpAction,
  taxonomy: State["taxonomy"],
): PageState => {
  const intersectingZoneIndex = state.zones.findIndex(isPointInZone(action.location));
  const newCurrentAction =
    intersectingZoneIndex >= 0 ? CurrentAction.HOVERING_ZONE : CurrentAction.HOVERING_NOTHING;
  const newZones: Array<Zone> = [];

  // User did a simple click on a zone while multiple zones were selected
  // so unselect the unclicked ones
  if (state.previousMouseLocation?.x === action.location.x && 
    state.previousMouseLocation?.y === action.location.y && 
    state.firstDragEvent
    && !action.ctrl && !action.shift) {
    if (state.activeZones.length > 1) {
      return {
        ...state,
        activeZones:[intersectingZoneIndex],
        currentAction:CurrentAction.HOVERING_ZONE
      }
    }
  }

  if (state.selection && state.activeZones.length < 1 && state.image) {
    let newZone = autoShrinkZone(
      {
        confidence: 1.0,
        humanGenerated: true,
        label: currentLabel,
        maxX: Math.min(
          action.imageSize.width,
          Math.max(state.selection.endx, state.selection.startx),
        ),
        maxY: Math.min(
          action.imageSize.height,
          Math.max(state.selection.endy, state.selection.starty),
        ),
        minX: Math.max(0, Math.min(state.selection.endx, state.selection.startx)),
        minY: Math.max(0, Math.min(state.selection.endy, state.selection.starty)),
      },
      state.image,
      state.noiseReductionLevel >= 0 ? state.noiseReductionLevel : 2,
      action.autoShrink,
    );
    if (isTableLabel(currentLabel, taxonomy)) {
      newZone = {
        ...newZone,
        cells: [
          {
            maxX: newZone.maxX,
            maxY: newZone.maxY,
            minX: newZone.minX,
            minY: newZone.minY,
          },
        ],
      };
    }
    const height = Math.ceil(newZone.maxY) - Math.floor(newZone.minY);
    const width = Math.ceil(newZone.maxX) - Math.floor(newZone.minX);
    if (width > 1 && height > 1) {
      // add a new zone
      newZones.push(newZone);
    }
  }

  if (
    state.selection ||
    state.currentAction !== newCurrentAction ||
    state.previousMouseLocation ||
    newZones.length > 0
  ) {
    return {
      ...state,
      activeZones: newZones.length > 0 ? [state.zones.length] : state.activeZones,
      currentAction: newCurrentAction,
      firstDragEvent: false,
      pageSaveState: PageSaveState.Dirty,
      previousMouseLocation: null,
      selection: null,
      zones:
        newZones.length > 0 ? state.zoneHistory.push(state.zones.concat(newZones)) : state.zones,
    };
  }
  return state;
};

export const mouseUp = (state: State, action: MouseUpAction): State => {
  const updatedPage = mouseUpOnPage(state.currentLabel)(
    state.pages[state.activePage],
    action,
    state.taxonomy,
  );
  if (updatedPage === state.pages[state.activePage]) {
    return state;
  }
  return {
    ...state,
    pages: [
      ...state.pages.slice(0, state.activePage),
      updatedPage,
      ...state.pages.slice(state.activePage + 1),
    ],
  };
};
